import { IconButton, InputAdornment, useTheme } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import isNil from 'lodash/isNil';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Wallet } from '@/api/wallets/types';
import { ReactComponent as FPSIcon } from '@/assets/icons/fps-big.svg';
import { ClosableDialog, HeaderTitle, ReadonlyItem, Space, TextFieldInput } from '@/components';
import { MoneyType, Value } from '@/model/common';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import { makeSelectWallet, makeSelectWallets } from '@/model/wallets/selectors';
import { WalletId } from '@/model/withdrawal/types';
import SingleWalletOption from '@/pages/withdrawal/components/SingleWalletOption';
import { Func } from '@/utils/ts';

interface WalletSelectionDialogProps {
  'data-test'?: string;
  selectConstraints?: {
    minBalance?: number;
  };
  onSelect: Func<[string]>;
  onClose: Func;
}

const selectIsMobile = makeSelectIsMobileView();
const selectWallets = makeSelectWallets();

const WalletSelectionDialog: React.FC<WalletSelectionDialogProps> = ({
  selectConstraints,
  onSelect,
  onClose,
  'data-test': dataTest,
}) => {
  const baseClasses = useTheme();
  const isMobile = useSelector(selectIsMobile);
  const { all: wallets } = useSelector(selectWallets);
  const isWalletDisabled = (balance: MoneyType): boolean =>
    !!selectConstraints?.minBalance && selectConstraints.minBalance > balance;
  const padding = baseClasses.spacing(isMobile ? 2 : 4);
  return (
    <ClosableDialog
      data-test={dataTest && `${dataTest}-container`}
      onClose={onClose}
      visible
      title={
        <HeaderTitle
          title={<FormattedMessage id="withdrawal.fields.wallet.dialog.title" />}
          postfix={!isMobile && <FPSIcon />}
        />
      }
    >
      {wallets.length > 0 && (
        <Space
          direction="vertical"
          size={2.5}
          style={{ overflow: 'auto', paddingLeft: padding, paddingRight: padding }}
        >
          {wallets.map((wallet: Wallet) => (
            <SingleWalletOption
              key={wallet.id}
              {...wallet}
              {...(isWalletDisabled(wallet.balance.amount)
                ? {
                    disabled: true,
                    error: <FormattedMessage id="withdrawal.fields.wallet.error.insufficientFunds" />,
                  }
                : {})}
              onSelect={(): Promise<void> | void => onSelect(`${wallet.id}`)}
            />
          ))}
        </Space>
      )}
    </ClosableDialog>
  );
};

export interface WalletItemProps {
  'data-test'?: string;
  value?: Value<WalletId>;
  isCredit?: boolean;
  readonly?: boolean;
  onChange?: (value: WalletId) => Promise<void> | void;
  selectConstraints?: {
    minBalance?: number;
  };
}

export const WalletItem: React.FC<WalletItemProps> = ({
  value,
  isCredit = true,
  selectConstraints,
  onChange,
  readonly,
  'data-test': dataTest,
}) => {
  const walletId = value?.value;
  const [walletsVisible, showWallets] = useState<boolean>(false);
  const selectWallet = useMemo(() => (walletId ? makeSelectWallet(walletId) : (): undefined => undefined), [walletId]);
  const wallet = useSelector(selectWallet);
  return readonly ? (
    <ReadonlyItem
      data-test={dataTest && `${dataTest}-value`}
      textClassName="ym-hide-content"
      label={
        isCredit ? (
          <FormattedMessage id="withdrawal.fields.wallet.label.credit" />
        ) : (
          <FormattedMessage id="withdrawal.fields.wallet.label.debit" />
        )
      }
    >
      {wallet?.id}
    </ReadonlyItem>
  ) : (
    <>
      <TextFieldInput
        data-test={dataTest && `${dataTest}-value`}
        InputProps={{
          className: 'ym-hide-content',
          readOnly: true,
          inputProps: {
            tabIndex: -1,
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                data-test={dataTest && `${dataTest}-search`}
                onClick={(): void => {
                  showWallets(true);
                }}
              >
                <KeyboardArrowDownIcon style={{ cursor: 'pointer' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        value={{ value: !isNil(walletId) ? `${walletId}` : undefined, hasError: !!value?.hasError }}
        messages={{
          label: isCredit ? (
            <FormattedMessage id="withdrawal.fields.wallet.label.credit" />
          ) : (
            <FormattedMessage id="withdrawal.fields.wallet.label.debit" />
          ),
          error: <FormattedMessage id="withdrawal.fields.wallet.error.common" />,
        }}
      />
      {walletsVisible && onChange && (
        <WalletSelectionDialog
          data-test={dataTest && `${dataTest}-dialog`}
          selectConstraints={selectConstraints}
          onSelect={async (selectedWallet: string): Promise<void> => {
            await onChange(parseInt(selectedWallet, 10));
            showWallets(false);
          }}
          onClose={(): void => showWallets(false)}
        />
      )}
    </>
  );
};
export default WalletItem;
